import { useState, useEffect, useCallback } from 'react';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, 
  XAxis, YAxis, Tooltip, Legend, CartesianGrid 
} from 'recharts';
import { 
  BarChart3, 
  Users, 
  Tractor, 
  Euro, 
  Clock, 
  Leaf,
  Building2,
  TrendingUp,
  Download,
  RefreshCw,
  Filter,
  Target,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const COLORS = [
  '#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', 
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'
];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border">
        <p className="font-semibold text-slate-800">{payload[0].name || label}</p>
        <p className="text-sm text-slate-600">
          {payload[0].dataKey === 'cout' || payload[0].name?.includes('€') 
            ? `${payload[0].value?.toLocaleString('fr-FR')} €`
            : payload[0].value?.toLocaleString('fr-FR')
          }
        </p>
        {payload[0].payload?.pourcentage_cout && (
          <p className="text-xs text-slate-500">
            {payload[0].payload.pourcentage_cout}% du total
          </p>
        )}
      </div>
    );
  }
  return null;
};

export default function AnalyseGeofoliaPage() {
  const { activeSociete } = useWorkspace();
  const [loading, setLoading] = useState(true);
  const [campagne, setCampagne] = useState('2025');
  const [activeTab, setActiveTab] = useState('couts');
  
  // Données
  const [analyseCouts, setAnalyseCouts] = useState(null);
  const [bilanOperateurs, setBilanOperateurs] = useState(null);
  const [rentabiliteCultures, setRentabiliteCultures] = useState(null);
  const [exploitationFilter, setExploitationFilter] = useState('');
  const [exploitations, setExploitations] = useState([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [coutsRes, operateursRes, rentabiliteRes, explRes] = await Promise.all([
        api.get(`/geofolia/analyse/couts-production?campagne=${campagne}${exploitationFilter ? `&exploitation=${exploitationFilter}` : ''}`),
        api.get(`/geofolia/bilan/operateurs`),
        api.get(`/geofolia/analyse/rentabilite-cultures?campagne=${campagne}`),
        api.get('/geofolia/exploitations')
      ]);
      
      setAnalyseCouts(coutsRes.data);
      setBilanOperateurs(operateursRes.data);
      setRentabiliteCultures(rentabiliteRes.data);
      setExploitations(explRes.data.exploitations || []);
    } catch (error) {
      console.error('Erreur chargement analyses:', error);
      toast.error('Erreur lors du chargement des analyses');
    } finally {
      setLoading(false);
    }
  }, [campagne, exploitationFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Préparer les données pour les camemberts
  const pieDataCategorie = analyseCouts?.par_categorie?.map((item, idx) => ({
    name: item.nom || 'Autre',
    value: item.cout,
    pourcentage: item.pourcentage_cout,
    fill: COLORS[idx % COLORS.length]
  })) || [];

  const pieDataCulture = analyseCouts?.par_culture?.slice(0, 8).map((item, idx) => ({
    name: item.nom || 'Non spécifié',
    value: item.cout,
    pourcentage: item.pourcentage_cout,
    fill: COLORS[idx % COLORS.length]
  })) || [];

  const barDataOperateurs = bilanOperateurs?.bilan?.slice(0, 10).map(op => ({
    name: op.operateur?.split(' ')[0] || 'N/A',
    heures: op.heures_totales,
    interventions: op.nb_interventions,
    surface: op.surface_totale
  })) || [];

  const barDataExploitations = analyseCouts?.par_exploitation?.slice(0, 8).map(expl => ({
    name: expl.nom?.substring(0, 15) || 'N/A',
    cout: expl.cout,
    heures: expl.heures,
    surface: expl.surface
  })) || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-6" data-testid="analyse-geofolia-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Analyse des Interventions</h1>
              <p className="text-slate-500">Coûts, opérateurs, rentabilité - Données Geofolia</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3 flex-wrap">
            <Select value={campagne} onValueChange={setCampagne}>
              <SelectTrigger className="w-32" data-testid="campagne-select">
                <SelectValue placeholder="Campagne" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2024">2024</SelectItem>
                <SelectItem value="2025">2025</SelectItem>
                <SelectItem value="2026">2026</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={exploitationFilter || "all"} onValueChange={(v) => setExploitationFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-48" data-testid="exploitation-filter">
                <SelectValue placeholder="Toutes exploitations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes exploitations</SelectItem>
                {exploitations.map(e => (
                  <SelectItem key={e.id} value={e.raison_sociale || e.id}>
                    {e.raison_sociale}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button variant="outline" onClick={loadData} disabled={loading} data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </div>
        </div>
      </div>

      {/* Stats rapides */}
      {analyseCouts?.totaux && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-white/80 backdrop-blur">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Euro className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">
                    {analyseCouts.totaux.cout_total?.toLocaleString('fr-FR')} €
                  </p>
                  <p className="text-xs text-slate-500">Coût total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white/80 backdrop-blur">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">
                    {analyseCouts.totaux.heures_totales?.toLocaleString('fr-FR')} h
                  </p>
                  <p className="text-xs text-slate-500">Heures totales</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white/80 backdrop-blur">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Target className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">
                    {analyseCouts.totaux.surface_totale?.toLocaleString('fr-FR')} ha
                  </p>
                  <p className="text-xs text-slate-500">Surface travaillée</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white/80 backdrop-blur">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">
                    {analyseCouts.totaux.cout_moyen_par_ha?.toLocaleString('fr-FR')} €/ha
                  </p>
                  <p className="text-xs text-slate-500">Coût moyen</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-white/80 backdrop-blur p-1">
          <TabsTrigger value="couts" className="data-[state=active]:bg-blue-500 data-[state=active]:text-white">
            <Euro className="w-4 h-4 mr-2" />
            Coûts
          </TabsTrigger>
          <TabsTrigger value="operateurs" className="data-[state=active]:bg-blue-500 data-[state=active]:text-white">
            <Users className="w-4 h-4 mr-2" />
            Opérateurs
          </TabsTrigger>
          <TabsTrigger value="exploitations" className="data-[state=active]:bg-blue-500 data-[state=active]:text-white">
            <Building2 className="w-4 h-4 mr-2" />
            Exploitations
          </TabsTrigger>
          <TabsTrigger value="cultures" className="data-[state=active]:bg-blue-500 data-[state=active]:text-white">
            <Leaf className="w-4 h-4 mr-2" />
            Cultures
          </TabsTrigger>
        </TabsList>

        {/* Tab Coûts - Camemberts */}
        <TabsContent value="couts" className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Camembert par catégorie */}
              <Card className="bg-white shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Tractor className="w-5 h-5 text-blue-600" />
                    Répartition par catégorie d'opération
                  </CardTitle>
                  <CardDescription>Coûts par type de travail agricole</CardDescription>
                </CardHeader>
                <CardContent>
                  {pieDataCategorie.length > 0 ? (
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pieDataCategorie}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={100}
                            paddingAngle={2}
                            dataKey="value"
                            label={({ name, pourcentage }) => `${name} (${pourcentage}%)`}
                            labelLine={{ stroke: '#64748b', strokeWidth: 1 }}
                          >
                            {pieDataCategorie.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Pie>
                          <Tooltip content={<CustomTooltip />} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-80 flex items-center justify-center text-slate-500">
                      <p>Aucune donnée disponible</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Camembert par culture */}
              <Card className="bg-white shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Leaf className="w-5 h-5 text-green-600" />
                    Répartition par culture
                  </CardTitle>
                  <CardDescription>Coûts par type de culture</CardDescription>
                </CardHeader>
                <CardContent>
                  {pieDataCulture.length > 0 ? (
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pieDataCulture}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={100}
                            paddingAngle={2}
                            dataKey="value"
                            label={({ name, pourcentage }) => `${name.substring(0, 12)}${name.length > 12 ? '...' : ''}`}
                            labelLine={{ stroke: '#64748b', strokeWidth: 1 }}
                          >
                            {pieDataCulture.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Pie>
                          <Tooltip content={<CustomTooltip />} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-80 flex items-center justify-center text-slate-500">
                      <p>Aucune donnée disponible</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Tableau détaillé des opérations */}
              <Card className="bg-white shadow-lg md:col-span-2">
                <CardHeader>
                  <CardTitle>Détail par opération</CardTitle>
                  <CardDescription>Coûts, heures et surfaces par type d'opération</CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[400px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Opération</TableHead>
                          <TableHead className="text-right">Coût (€)</TableHead>
                          <TableHead className="text-right">% du total</TableHead>
                          <TableHead className="text-right">Heures</TableHead>
                          <TableHead className="text-right">Surface (ha)</TableHead>
                          <TableHead className="text-right">€/ha</TableHead>
                          <TableHead className="text-right">Interventions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {analyseCouts?.par_operation?.map((op, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{op.nom}</TableCell>
                            <TableCell className="text-right font-semibold text-green-600">
                              {op.cout?.toLocaleString('fr-FR')} €
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Progress value={op.pourcentage_cout} className="w-16 h-2" />
                                <span className="text-sm w-12">{op.pourcentage_cout}%</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">{op.heures?.toLocaleString('fr-FR')} h</TableCell>
                            <TableCell className="text-right">{op.surface?.toLocaleString('fr-FR')} ha</TableCell>
                            <TableCell className="text-right text-blue-600">{op.cout_par_ha?.toLocaleString('fr-FR')} €</TableCell>
                            <TableCell className="text-right">
                              <Badge variant="secondary">{op.nb_interventions}</Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Tab Opérateurs */}
        <TabsContent value="operateurs" className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : (
            <>
              {/* Graphique à barres des heures par opérateur */}
              <Card className="bg-white shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-purple-600" />
                    Heures par opérateur
                  </CardTitle>
                  <CardDescription>Répartition du temps de travail</CardDescription>
                </CardHeader>
                <CardContent>
                  {barDataOperateurs.length > 0 ? (
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={barDataOperateurs} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis dataKey="name" type="category" width={100} />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="heures" fill="#8b5cf6" name="Heures" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-80 flex items-center justify-center text-slate-500">
                      <p>Aucune donnée disponible</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Tableau détaillé opérateurs */}
              <Card className="bg-white shadow-lg">
                <CardHeader>
                  <CardTitle>Bilan détaillé par opérateur</CardTitle>
                  <CardDescription>Heures, interventions et surfaces par collaborateur</CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[500px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Opérateur</TableHead>
                          <TableHead className="text-right">Heures</TableHead>
                          <TableHead className="text-right">Interventions</TableHead>
                          <TableHead className="text-right">Surface (ha)</TableHead>
                          <TableHead className="text-right">Exploitations</TableHead>
                          <TableHead>Opérations principales</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bilanOperateurs?.bilan?.map((op, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                                  <Users className="w-4 h-4 text-purple-600" />
                                </div>
                                <span className="font-medium">{op.operateur}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-semibold text-purple-600">
                              {op.heures_totales?.toLocaleString('fr-FR')} h
                            </TableCell>
                            <TableCell className="text-right">
                              <Badge variant="outline">{op.nb_interventions}</Badge>
                            </TableCell>
                            <TableCell className="text-right">{op.surface_totale?.toLocaleString('fr-FR')} ha</TableCell>
                            <TableCell className="text-right">
                              <Badge variant="secondary">{op.nb_exploitations}</Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {Object.entries(op.operations || {}).slice(0, 3).map(([opName, count]) => (
                                  <Badge key={opName} variant="outline" className="text-xs">
                                    {opName.substring(0, 15)}: {count}
                                  </Badge>
                                ))}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Tab Exploitations */}
        <TabsContent value="exploitations" className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : (
            <>
              {/* Graphique à barres des coûts par exploitation */}
              <Card className="bg-white shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-amber-600" />
                    Coûts par exploitation
                  </CardTitle>
                  <CardDescription>Répartition des coûts par client</CardDescription>
                </CardHeader>
                <CardContent>
                  {barDataExploitations.length > 0 ? (
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={barDataExploitations}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                          <YAxis />
                          <Tooltip 
                            formatter={(value, name) => [
                              name === 'cout' ? `${value?.toLocaleString('fr-FR')} €` : value?.toLocaleString('fr-FR'),
                              name === 'cout' ? 'Coût' : name === 'heures' ? 'Heures' : 'Surface (ha)'
                            ]}
                          />
                          <Legend />
                          <Bar dataKey="cout" fill="#f59e0b" name="Coût (€)" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-80 flex items-center justify-center text-slate-500">
                      <p>Aucune donnée disponible</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Tableau détaillé exploitations */}
              <Card className="bg-white shadow-lg">
                <CardHeader>
                  <CardTitle>Détail par exploitation</CardTitle>
                  <CardDescription>Coûts, heures et rentabilité par client</CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[500px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Exploitation</TableHead>
                          <TableHead className="text-right">Coût total (€)</TableHead>
                          <TableHead className="text-right">% du total</TableHead>
                          <TableHead className="text-right">Heures</TableHead>
                          <TableHead className="text-right">Surface (ha)</TableHead>
                          <TableHead className="text-right">€/ha</TableHead>
                          <TableHead className="text-right">Interventions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {analyseCouts?.par_exploitation?.map((expl, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                                  <Building2 className="w-4 h-4 text-amber-600" />
                                </div>
                                <span className="font-medium">{expl.nom}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-semibold text-amber-600">
                              {expl.cout?.toLocaleString('fr-FR')} €
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Progress value={expl.pourcentage_cout} className="w-16 h-2" />
                                <span className="text-sm w-12">{expl.pourcentage_cout}%</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">{expl.heures?.toLocaleString('fr-FR')} h</TableCell>
                            <TableCell className="text-right">{expl.surface?.toLocaleString('fr-FR')} ha</TableCell>
                            <TableCell className="text-right text-blue-600">{expl.cout_par_ha?.toLocaleString('fr-FR')} €</TableCell>
                            <TableCell className="text-right">
                              <Badge variant="secondary">{expl.nb_interventions}</Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Tab Cultures/Rentabilité */}
        <TabsContent value="cultures" className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : (
            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Leaf className="w-5 h-5 text-green-600" />
                  Rentabilité par culture
                </CardTitle>
                <CardDescription>Analyse des coûts et rendements par type de culture</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[600px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Culture</TableHead>
                        <TableHead className="text-right">Surface (ha)</TableHead>
                        <TableHead className="text-right">Parcelles</TableHead>
                        <TableHead className="text-right">Coût total (€)</TableHead>
                        <TableHead className="text-right">Coût/ha (€)</TableHead>
                        <TableHead className="text-right">Heures</TableHead>
                        <TableHead className="text-right">Heures/ha</TableHead>
                        <TableHead className="text-right">Interventions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rentabiliteCultures?.cultures?.map((cult, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div 
                                className="w-3 h-8 rounded-full"
                                style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                              />
                              <span className="font-medium">{cult.culture}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            {cult.surface_totale?.toLocaleString('fr-FR')} ha
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge variant="outline">{cult.nb_parcelles}</Badge>
                          </TableCell>
                          <TableCell className="text-right text-green-600 font-semibold">
                            {cult.cout_total?.toLocaleString('fr-FR')} €
                          </TableCell>
                          <TableCell className="text-right text-blue-600">
                            {cult.cout_par_ha?.toLocaleString('fr-FR')} €
                          </TableCell>
                          <TableCell className="text-right">
                            {cult.heures_totales?.toLocaleString('fr-FR')} h
                          </TableCell>
                          <TableCell className="text-right text-purple-600">
                            {cult.heures_par_ha?.toLocaleString('fr-FR')} h
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge variant="secondary">{cult.nb_interventions}</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
